/**
 * Input limits — the single frontend table of per-field ranges.
 *
 * Mirrors the backend (models.py is the source of truth) and the signed-off
 * spec in docs/input-limits.md. Two tiers per field:
 *
 *   soft  Normal Jackstone design range. Values outside it are clamped /
 *         snapped on blur by lib/autocorrect — unless the user appends "!".
 *   hard  Absolute limit. Outside it the field shows a red error and the
 *         calculation is blocked (lib/validators). "!" does NOT bypass it.
 *
 * Where soft = hard there is effectively no "!" override for that field.
 * Linked Gantt fields reuse the Calculator entries (see GANTT_LIMITS) so both
 * pages run exactly one rule set.
 */
import type { CalcInputs } from "@/types/calc";

export interface SoftRange {
  min: number;
  max: number;
  step?: number;                // snap to multiples of step (e.g. 10 mm)
  snap?: readonly number[];     // snap to the nearest value in this sorted set
}

export interface HardRange {
  min: number;                  // -Infinity = no lower limit
  max: number;                  // Infinity  = no upper limit
}

export interface FieldLimit {
  unit: string;                 // "" for dimensionless counts
  soft?: SoftRange;             // omitted = no autocorrect
  hard: HardRange;
  integer?: boolean;            // whole numbers only
  decimals: number;             // display / entry precision
  strictDecimals?: boolean;     // more than `decimals` places is an error
}

// Standard plate spacings: 5 mm steps 50–120, with 76 replacing 75 and 102
// replacing 100 (physical spacer corrections).
export const T_STATION_SNAP = [50, 55, 60, 65, 70, 76, 80, 85, 90, 95, 102, 105, 110, 115, 120] as const;

// ── Calculator fields ────────────────────────────────────────────────────────

export const CALC_LIMITS: Partial<Record<keyof CalcInputs, FieldLimit>> = {
  // Product Properties
  inletTemp:  { unit: "°C",    soft: { min: 2,   max: 35 },   hard: { min: 2,   max: 45 },   decimals: 1 },
  outletTemp: { unit: "°C",    soft: { min: -23, max: -10 },  hard: { min: -25, max: -10 },  decimals: 1 },
  density:    { unit: "kg/m³", soft: { min: 700, max: 1200 }, hard: { min: 700, max: 1200 }, decimals: 0 },
  contactPc:  { unit: "%",     soft: { min: 50,  max: 100 },  hard: { min: 50,  max: 100 },  decimals: 0 },

  // Refrigerant
  evapTemp:     { unit: "°C", soft: { min: -50, max: -30 }, hard: { min: -52, max: Infinity }, decimals: 1 },
  // Floor is the fluid's rated minimum (number in its label) — a cross-field
  // rule in lib/validators, so no static hard range here.
  htfInletTemp: { unit: "°C", hard: { min: -Infinity, max: Infinity }, decimals: 1 },
  htfDeltaT:    { unit: "K",  soft: { min: 2, max: 6 }, hard: { min: 0.5, max: 15 }, decimals: 1 },

  // Freezer Sizing
  nFreezers: { unit: "",   hard: { min: 1, max: 20 }, integer: true, decimals: 0 },
  nStations: { unit: "",   soft: { min: 6, max: 48 }, hard: { min: 1, max: 60 }, integer: true, decimals: 0 },
  lStation:  { unit: "mm", soft: { min: 750, max: 1260, step: 10 }, hard: { min: 300, max: 2000 }, decimals: 0 },
  dStation:  { unit: "mm", soft: { min: 400, max: 800,  step: 10 }, hard: { min: 200, max: 1060 }, decimals: 0 },
  tStation:  { unit: "mm", soft: { min: 50,  max: 120, snap: T_STATION_SNAP }, hard: { min: 50, max: 150 }, decimals: 0 },
  nDividers: { unit: "",   hard: { min: 0, max: 4 }, integer: true, decimals: 0 },

  // Cycle times
  loadingTime:   { unit: "s/stn", soft: { min: 5, max: 60 }, hard: { min: 0, max: 300 }, decimals: 0 },
  unloadingTime: { unit: "s/stn", soft: { min: 5, max: 60 }, hard: { min: 0, max: 300 }, decimals: 0 },
  waitMisc:      { unit: "min",   hard: { min: 0, max: 60 }, decimals: 1 },
  // MAN overrides — blank = AUTO (validators skip blank values)
  overrideFreezeTime: { unit: "min", hard: { min: 10,  max: 1440 }, integer: true, decimals: 0 },
  overrideDefrostMin: { unit: "min", hard: { min: 0.5, max: 10 },   decimals: 1, strictDecimals: true },

  // Shift length — same field as the Gantt shift length
  cyclesWindowHrs: { unit: "h", hard: { min: 1, max: 24 }, decimals: 1 },
};

// Minimum temperature difference rules (K) — mirror backend validate().
export const MIN_PRODUCT_DT_K = 10;     // inlet − outlet ≥ 10 K
export const MIN_APPROACH_K = 10;       // evap (or fluid outlet) ≤ outlet − 10 K
export const FREEZE_POINT_MARGIN_K = 1; // outlet ≤ product freezing point − 1 K
export const MAX_SHIFT_PLUS_OT_H = 24;  // Gantt: shift + overtime ≤ 24 h

// ── Production Gantt fields ──────────────────────────────────────────────────
//
// Keys are GanttInputs field names (kept as plain strings so this module does
// not import from the Gantt components). Linked fields point at the SAME
// Calculator entries — one rule set.

export const GANTT_LIMITS: Record<string, FieldLimit> = {
  // Gantt-own
  mFlow:             { unit: "kg/h",      hard: { min: 1,   max: 50000 }, decimals: 0 },
  overtimeH:         { unit: "h",         hard: { min: 0,   max: 12 },    decimals: 1 },
  startStockBatches: { unit: "batches",   hard: { min: 0,   max: 50 },    decimals: 0 },
  goalT:             { unit: "t / shift", hard: { min: 0.1, max: 1000 },  decimals: 1 },
  closingSec:        { unit: "s",         hard: { min: 5,   max: 600 },   decimals: 0 },
  openingSec:        { unit: "s",         hard: { min: 5,   max: 600 },   decimals: 0 },
  nCrews:            { unit: "",          hard: { min: 1,   max: 4 },     integer: true, decimals: 0 },
  // Linked to the Calculator
  tShift:          CALC_LIMITS.cyclesWindowHrs!,
  nFreezers:       CALC_LIMITS.nFreezers!,
  nStations:       CALC_LIMITS.nStations!,
  lStation:        CALC_LIMITS.lStation!,
  dStation:        CALC_LIMITS.dStation!,
  tStation:        CALC_LIMITS.tStation!,
  nDividers:       CALC_LIMITS.nDividers!,
  rhoProduct:      CALC_LIMITS.density!,
  loadingSec:      CALC_LIMITS.loadingTime!,
  unloadingSec:    CALC_LIMITS.unloadingTime!,
  waitingMin:      CALC_LIMITS.waitMisc!,
  freezeOverride:  CALC_LIMITS.overrideFreezeTime!,
  defrostOverride: CALC_LIMITS.overrideDefrostMin!,
};

// Optimiser (Auto-optimise plant card)
export const OPT_LIMITS = {
  flowMargin:   { unit: "%", hard: { min: 0, max: 50 }, decimals: 0 } as FieldLimit,  // flow headroom
  freezeMargin: { unit: "%", hard: { min: 0, max: 50 }, decimals: 0 } as FieldLimit,  // safety margin
  maxCrews:     { unit: "",  hard: { min: 1, max: 4 }, integer: true, decimals: 0 } as FieldLimit,
};

export const DEFAULT_FLOW_MARGIN_PCT = 10;
export const DEFAULT_SAFETY_MARGIN_PCT = 10;

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Number with a proper minus sign (−25) for messages. */
export function fmtLimitNum(x: number): string {
  const r = Math.round(x * 1000) / 1000;   // hide float noise (−2.2 − 1)
  return r < 0 ? `−${Math.abs(r)}` : String(r);
}

/** Human-readable hard range, e.g. "2–45 °C", "≥ −52 °C", "−25 to −10 °C". */
export function fmtHardRange(l: FieldLimit): string {
  const u = l.unit ? ` ${l.unit}` : "";
  const { min, max } = l.hard;
  if (!isFinite(min) && !isFinite(max)) return "";
  if (!isFinite(max)) return `≥ ${fmtLimitNum(min)}${u}`;
  if (!isFinite(min)) return `≤ ${fmtLimitNum(max)}${u}`;
  // An en dash between two negatives ("−25–−10") reads badly — use "to".
  const sep = min < 0 || max < 0 ? " to " : "–";
  return `${fmtLimitNum(min)}${sep}${fmtLimitNum(max)}${u}`;
}

/**
 * Hard-limit error for a field value, or null when it is within limits.
 * The "!" override marker is stripped first — it never bypasses a hard limit.
 * Blank / non-numeric values return null: callers report those separately
 * ("Enter a number", or AUTO for blank overrides).
 */
export function hardLimitError(l: FieldLimit | undefined, raw: string): string | null {
  if (!l) return null;
  const cleaned = (raw || "").replace(/!/g, "").trim();
  if (cleaned === "") return null;
  const x = Number(cleaned);
  if (Number.isNaN(x)) return null;
  const range = fmtHardRange(l);
  if (l.integer && !Number.isInteger(x)) {
    return range ? `Must be a whole number ${range}` : "Must be a whole number";
  }
  if (x < l.hard.min || x > l.hard.max) return `Must be ${range}`;
  if (l.strictDecimals) {
    const dp = (cleaned.split(".")[1] ?? "").length;
    if (dp > l.decimals) return `Max ${l.decimals} decimal place${l.decimals === 1 ? "" : "s"}`;
  }
  return null;
}

/** Clamp x to the field's hard range (used where inputs are clamped on blur). */
export function clampHard(l: FieldLimit, x: number): number {
  return Math.min(l.hard.max, Math.max(l.hard.min, x));
}

/** min / max attributes for an <input type="number"> (open ends omitted). */
export function inputBounds(l: FieldLimit): { min?: number; max?: number } {
  return {
    ...(isFinite(l.hard.min) ? { min: l.hard.min } : {}),
    ...(isFinite(l.hard.max) ? { max: l.hard.max } : {}),
  };
}

/**
 * Rated minimum temperature of a heat-transfer fluid — the negative number in
 * its label: "HyCool -45" → −45, "Temper -55" → −55. null if none is found.
 */
export function htfRatedMin(label: string): number | null {
  const m = /-\s*(\d+(?:\.\d+)?)/.exec(label || "");
  return m ? -Number(m[1]) : null;
}
